/**
 * Design Helper Utilities
 * 
 * SOLID Principles:
 * - Single Responsibility: Each function does one specific thing
 * - KISS: Simple, focused functions
 * 
 * These utilities help transform canvas data to backend DTOs
 */

import { ICreateDesignDto, DesignSide } from '../types/design.types';
import type { PrintSide } from '../config/printTemplates';

/** True when a Fabric canvas JSON side has at least one object. */
export const canvasSideHasContent = (
  side: Record<string, unknown> | null | undefined,
): boolean => {
  if (!side || typeof side !== 'object' || Array.isArray(side)) {
    return false;
  }

  if (Array.isArray(side.objects) && side.objects.length > 0) {
    return true;
  }

  return Object.keys(side).length > 0;
};

/** Sides the buyer actually designed (one, both, never neither). */
export const getDesignedSides = (
  frontCanvasJSON: Record<string, unknown> | null | undefined,
  backCanvasJSON: Record<string, unknown> | null | undefined,
): PrintSide[] => {
  const sides: PrintSide[] = [];
  if (canvasSideHasContent(frontCanvasJSON)) sides.push('front');
  if (canvasSideHasContent(backCanvasJSON)) sides.push('back');
  return sides;
};

/**
 * Convert canvas state to CreateDesignDto format
 * This bridges the gap between frontend canvas and backend expectations
 * 
 * @param previewUrls - Optional pre-generated preview URLs (if not provided, backend can generate)
 */
export const prepareDesignForSave = (
  title: string,
  frontCanvasJSON: Record<string, any>,
  backCanvasJSON: Record<string, any>,
  options?: {
    productType?: string;
    color?: string;
    defaultSide?: DesignSide;
    frontPreviewUrl?: string | null;
    backPreviewUrl?: string | null;
  }
): ICreateDesignDto => {
  // ✅ Check if sides have content to determine preview URLs
  // If a side is empty, explicitly set preview URL to null to clear old previews
  const hasFrontContent = Boolean(
    frontCanvasJSON && 
    typeof frontCanvasJSON === 'object' && 
    !Array.isArray(frontCanvasJSON) &&
    (Array.isArray(frontCanvasJSON.objects) ? 
      frontCanvasJSON.objects.length > 0 : 
      Object.keys(frontCanvasJSON).length > 0)
  );

  const hasBackContent = Boolean(
    backCanvasJSON && 
    typeof backCanvasJSON === 'object' && 
    !Array.isArray(backCanvasJSON) &&
    (Array.isArray(backCanvasJSON.objects) ? 
      backCanvasJSON.objects.length > 0 : 
      Object.keys(backCanvasJSON).length > 0)
  );

  // ✅ CRITICAL: If side has no content, explicitly set preview URL to null
  // This ensures old preview URLs are cleared when a side is emptied
  const frontPreviewUrl = hasFrontContent 
    ? (options?.frontPreviewUrl || null)
    : null; // Explicitly null if side is empty

  const backPreviewUrl = hasBackContent 
    ? (options?.backPreviewUrl || null)
    : null; // Explicitly null if side is empty

  return {
    title,
    // Empty sides stored as {} (empty object) - standard JSONB pattern
    // Detection logic checks objects.length > 0 or Object.keys().length > 0
    frontSide: frontCanvasJSON || {},
    backSide: backCanvasJSON || {},
    // ✅ CRITICAL: Explicitly set null for empty sides to clear old preview URLs
    frontPreviewUrl,
    backPreviewUrl,
    defaultSide: options?.defaultSide || DesignSide.FRONT,
    productType: options?.productType || 'tshirt',
    color: options?.color || '#ffffff',
    metadata: {
      version: '1.0',
      editor: 'fabric.js',
      timestamp: new Date().toISOString(),
    },
  };
};

/**
 * Generate preview image from canvas
 * Returns a base64 string or data URL
 */
export const generateCanvasPreview = (
  canvas: any,
  options?: {
    format?: 'png' | 'jpeg';
    quality?: number;
    multiplier?: number;
  }
): string => {
  if (!canvas) return '';
  
  try {
    return canvas.toDataURL({
      format: options?.format || 'png',
      quality: options?.quality || 1,
      multiplier: options?.multiplier || 2,
      enableRetinaScaling: true,
    });
  } catch (error) {
    console.error('Failed to generate preview:', error);
    return '';
  }
};

/**
 * Validate design before saving
 * 
 * ✅ Enhanced: More robust validation that checks:
 * - Objects array length (if exists)
 * - Object keys length (if no objects array)
 * - Handles null/undefined gracefully
 */
export const validateDesign = (
  frontCanvasJSON: Record<string, any> | null,
  backCanvasJSON: Record<string, any> | null
): { isValid: boolean; error?: string } => {
  const hasContent = canvasSideHasContent;

  const hasFront = hasContent(frontCanvasJSON);
  const hasBack = hasContent(backCanvasJSON);

  // Both sides must not be empty
  if (!hasFront && !hasBack) {
    return {
      isValid: false,
      error: 'Cannot save empty design. Please add some content to at least one side.',
    };
  }

  return { isValid: true };
};

/**
 * Check if design has unsaved changes
 * Compare current canvas state with saved state
 */
export const hasUnsavedChanges = (
  currentFrontJSON: Record<string, any>,
  currentBackJSON: Record<string, any>,
  savedFrontJSON?: Record<string, any>,
  savedBackJSON?: Record<string, any>
): boolean => {
  if (!savedFrontJSON && !savedBackJSON) return true;
  
  return (
    JSON.stringify(currentFrontJSON) !== JSON.stringify(savedFrontJSON) ||
    JSON.stringify(currentBackJSON) !== JSON.stringify(savedBackJSON)
  );
};



